import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="image-viewer"
export default class extends Controller {
  static targets = ["image", "thumbnail", "modal", "modalImage", "caption", "counter"]
  static values = { 
    images: Array,
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    this.bindKeyboardEvents()
  }

  disconnect() {
    this.unbindKeyboardEvents()
  }

  openModal(event) {
    if (event) event.preventDefault()
    
    // Create modal if it doesn't exist
    if (!document.querySelector('.image-modal')) {
      this.createModal()
    }
    
    const modal = document.querySelector('.image-modal')
    const modalImage = modal.querySelector('.modal-image')
    const caption = modal.querySelector('.image-caption')
    const counter = modal.querySelector('.image-counter')
    
    // Set current image
    const clickedImage = event?.target || this.imageTarget
    const imageSrc = clickedImage.src || clickedImage.getAttribute('data-src')
    const imageAlt = clickedImage.alt || ''
    
    // Find index of clicked image
    if (this.hasImagesValue) {
      this.currentIndexValue = this.imagesValue.findIndex(img => img.src === imageSrc)
    }
    
    modalImage.src = imageSrc
    modalImage.alt = imageAlt
    
    if (caption) {
      caption.textContent = imageAlt
    }
    
    if (counter && this.hasImagesValue) {
      counter.textContent = `${this.currentIndexValue + 1} of ${this.imagesValue.length}`
    }
    
    // Show modal with animation
    modal.classList.remove('hidden')
    setTimeout(() => {
      modal.classList.add('active')
    }, 10)
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
  }

  closeModal() {
    const modal = document.querySelector('.image-modal')
    if (modal) {
      modal.classList.remove('active')
      setTimeout(() => {
        modal.classList.add('hidden')
        document.body.style.overflow = ''
      }, 300)
    }
  }

  nextImage() {
    if (this.hasImagesValue && this.currentIndexValue < this.imagesValue.length - 1) {
      this.currentIndexValue++
      this.updateModalImage()
    }
  }

  previousImage() {
    if (this.hasImagesValue && this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.updateModalImage()
    }
  }

  updateModalImage() {
    const modal = document.querySelector('.image-modal')
    if (!modal || !this.hasImagesValue) return
    
    const modalImage = modal.querySelector('.modal-image')
    const caption = modal.querySelector('.image-caption')
    const counter = modal.querySelector('.image-counter')
    
    const currentImage = this.imagesValue[this.currentIndexValue]
    
    // Fade out
    modalImage.style.opacity = '0'
    
    setTimeout(() => {
      modalImage.src = currentImage.src
      modalImage.alt = currentImage.alt || ''
      
      if (caption) {
        caption.textContent = currentImage.alt || ''
      }
      
      if (counter) {
        counter.textContent = `${this.currentIndexValue + 1} of ${this.imagesValue.length}`
      }
      
      // Fade in
      modalImage.style.opacity = '1'
    }, 150)
  }

  createModal() {
    const modal = document.createElement('div')
    modal.className = 'image-modal fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 hidden'
    modal.innerHTML = `
      <div class="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-4">
        <!-- Close button -->
        <button class="close-btn absolute top-4 right-4 text-white hover:text-gray-300 text-2xl z-10" onclick="document.querySelector('.image-modal').dispatchEvent(new CustomEvent('close'))">
          <i class="fas fa-times"></i>
        </button>
        
        <!-- Navigation buttons -->
        ${this.hasImagesValue && this.imagesValue.length > 1 ? `
          <button class="prev-btn absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10" onclick="document.querySelector('.image-modal').dispatchEvent(new CustomEvent('prev'))">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="next-btn absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10" onclick="document.querySelector('.image-modal').dispatchEvent(new CustomEvent('next'))">
            <i class="fas fa-chevron-right"></i>
          </button>
        ` : ''}
        
        <!-- Image -->
        <img class="modal-image max-w-full max-h-full object-contain transition-opacity duration-300" src="" alt="">
        
        <!-- Caption and counter -->
        <div class="absolute bottom-4 left-4 right-4 text-center">
          <div class="image-caption text-white text-sm mb-2"></div>
          ${this.hasImagesValue && this.imagesValue.length > 1 ? '<div class="image-counter text-white text-xs opacity-75"></div>' : ''}
        </div>
        
        <!-- Loading spinner -->
        <div class="loading-spinner absolute inset-0 flex items-center justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    `
    
    // Add event listeners
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.classList.contains('max-w-7xl')) {
        this.closeModal()
      }
    })
    
    modal.addEventListener('close', () => this.closeModal())
    modal.addEventListener('prev', () => this.previousImage())
    modal.addEventListener('next', () => this.nextImage())
    
    // Handle image loading
    const modalImage = modal.querySelector('.modal-image')
    const spinner = modal.querySelector('.loading-spinner')
    
    modalImage.addEventListener('load', () => {
      spinner.style.display = 'none'
    })
    
    modalImage.addEventListener('loadstart', () => {
      spinner.style.display = 'flex'
    })
    
    document.body.appendChild(modal)
  }

  handleKeyboardEvent = (event) => {
    if (!document.querySelector('.image-modal.active')) return
    
    switch(event.key) {
      case 'Escape':
        event.preventDefault()
        this.closeModal()
        break
      case 'ArrowLeft':
        event.preventDefault()
        this.previousImage()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.nextImage()
        break
      case ' ':
        event.preventDefault()
        this.nextImage()
        break
    }
  }

  bindKeyboardEvents() {
    document.addEventListener('keydown', this.handleKeyboardEvent)
  }

  unbindKeyboardEvents() {
    document.removeEventListener('keydown', this.handleKeyboardEvent)
  }

  // Zoom functionality
  zoomIn() {
    const modalImage = document.querySelector('.modal-image')
    if (modalImage) {
      const currentScale = parseFloat(modalImage.style.transform?.match(/scale\(([\d.]+)\)/)?.[1] || 1)
      const newScale = Math.min(currentScale * 1.2, 3)
      modalImage.style.transform = `scale(${newScale})`
      modalImage.style.cursor = newScale > 1 ? 'grab' : 'zoom-in'
    }
  }

  zoomOut() {
    const modalImage = document.querySelector('.modal-image')
    if (modalImage) {
      const currentScale = parseFloat(modalImage.style.transform?.match(/scale\(([\d.]+)\)/)?.[1] || 1)
      const newScale = Math.max(currentScale / 1.2, 0.5)
      modalImage.style.transform = `scale(${newScale})`
      modalImage.style.cursor = newScale > 1 ? 'grab' : 'zoom-in'
    }
  }

  resetZoom() {
    const modalImage = document.querySelector('.modal-image')
    if (modalImage) {
      modalImage.style.transform = 'scale(1)'
      modalImage.style.cursor = 'zoom-in'
    }
  }
}
